// filepath: resources/assets/front/js/components/gallery.js

/**
 * Gallery Component
 * 
 * Gestion des galeries avec:
 * - Lightbox natif (sans dépendance externe)
 * - Lazy loading intelligent avec IntersectionObserver
 * - Support WebP automatique
 * - Navigation clavier
 * - Autosize des images
 * - Support tactile (swipe)
 */

import Splide from '@splidejs/splide';

// Store des instances de galeries
const galleries = new Map();

// Store des instances Splide pour les carousels
const carouselInstances = new Map();


// Configuration par défaut
const defaultConfig = {
    columns: 3,
    gap: 'medium',
    aspectRatio: '1/1',
    rounded: 'rounded-lg',
    lightbox: true,
    showCaptions: true,
    keyboardNavigation: true,
    hoverEffect: 'zoom',
    imageShadow: 'shadow-sm',
    layoutType: 'grid',
    lazyLoad: true,
    lazyLoadThreshold: 0.1, // 10% de visibilité avant chargement
    lazyLoadRootMargin: '100px', // Précharger 100px avant
    preloadAdjacent: true, // Précharger les images adjacentes dans le lightbox
    // ✅ Options carousel (Splide)
    carousel: {
        autoplay: true,
        interval: 5000,
        speed: 600,
        arrows: true,
        pagination: true,
        loop: true,
        drag: true,
        perMove: 1,
        pauseOnHover: true,
        pauseOnFocus: true,
        rewind: false,
        breakpoints: {
            640: { perPage: 1 },
            768: { perPage: 2 },
            1024: { perPage: 3 },
        }
    }
};

// Map pour convertir les valeurs de gap en pixels
const gapMap = {
    'none': '0px',
    'small': '0.5rem',
    'medium': '1rem',
    'large': '1.5rem',
    'xlarge': '2rem',
};

/**
 * Initialiser toutes les galeries dans un élément
 * @param {HTMLElement} container - Élément conteneur
 */
export function initGallery(container = document) {
    const galleryElements = container.querySelectorAll('.builder-gallery');
    
    galleryElements.forEach((element, index) => {
        if (element.hasAttribute('data-gallery-initialized')) {
            return;
        }
        
        const galleryId = element.id || `gallery-${Date.now()}-${index}`;
        element.id = galleryId;
        element.setAttribute('data-gallery-initialized', 'true');
        
        const gallery = new Gallery(element, galleryId);
        galleries.set(galleryId, gallery);
        
        console.log(`📸 Gallery initialized: ${galleryId} (${gallery.config.layoutType})`);
    });
}

/**
 * Classe Gallery
 */
class Gallery {
    constructor(element, id) {
        this.element = element;
        this.id = id;
        this.config = this.parseConfig();
        this.items = [];
        this.currentIndex = 0;
        this.lightboxElement = null;
        this.isLightboxOpen = false;
        this.touchStartX = 0;
        this.touchEndX = 0;
        this.splideInstance = null;
        
        this.init();
    }
    
    /**
     * Parser la configuration depuis les data attributes
     */
    parseConfig() {
        const dataset = this.element.dataset;
        
        return {
            columns: parseInt(dataset.galleryColumns) || defaultConfig.columns,
            gap: dataset.galleryGap || defaultConfig.gap,
            aspectRatio: dataset.galleryAspectRatio || defaultConfig.aspectRatio,
            rounded: dataset.galleryRounded || defaultConfig.rounded,
            lightbox: dataset.galleryLightbox !== 'false',
            showCaptions: dataset.galleryShowCaptions !== 'false',
            keyboardNavigation: dataset.galleryKeyboardNavigation !== 'false',
            hoverEffect: dataset.galleryHoverEffect || defaultConfig.hoverEffect,
            imageShadow: dataset.galleryImageShadow || defaultConfig.imageShadow,
            layoutType: dataset.galleryLayoutType || defaultConfig.layoutType,
            count: parseInt(dataset.galleryCount) || 0,
            lazyLoad: dataset.galleryLazyLoad !== 'false',
            lazyLoadThreshold: parseFloat(dataset.galleryLazyThreshold) || defaultConfig.lazyLoadThreshold,
            lazyLoadRootMargin: dataset.galleryLazyMargin || defaultConfig.lazyLoadRootMargin,
            preloadAdjacent: dataset.galleryPreloadAdjacent !== 'false',
            
            // ✅ Options carousel
            carousel: {
                autoplay: dataset.galleryAutoplay !== 'false',
                interval: parseInt(dataset.galleryInterval) || defaultConfig.carousel.interval,
                speed: parseInt(dataset.gallerySpeed) || defaultConfig.carousel.speed,
                arrows: dataset.galleryArrows !== 'false',
                pagination: dataset.galleryPagination !== 'false',
                loop: dataset.galleryLoop !== 'false',
                drag: dataset.galleryDrag !== 'false',
                perMove: parseInt(dataset.galleryPerMove) || defaultConfig.carousel.perMove,
                pauseOnHover: dataset.galleryPauseOnHover !== 'false',
                pauseOnFocus: dataset.galleryPauseOnFocus !== 'false',
                rewind: dataset.galleryRewind === 'true',
            }
        };
    }
    
    /**
     * Initialiser la galerie
     */
    init() {
        this.parseItems();
        
        // ✅ Si c'est un carousel, utiliser Splide
        if (this.config.layoutType === 'carousel') {
            this.initCarousel();
            return;
        }
        
        // Sinon, comportement normal (grid, masonry, justified)
        if (this.config.lazyLoad) {
            this.initLazyLoad();
        }
        
        if (this.config.lightbox) {
            this.initLightbox();
        }
        
        this.bindEvents();
    }
    
    /**
     * Parser les items de la galerie
     */
    parseItems() {
        const itemElements = this.element.querySelectorAll('.gallery-item');
        
        this.items = Array.from(itemElements).map((item, index) => ({
            element: item,
            index: parseInt(item.dataset.itemIndex) || index,
            id: item.dataset.itemId,
            version: item.dataset.itemVersion || 'original',
            src: item.dataset.itemSrc,
            srcWebp: item.dataset.itemSrcWebp,
            fullSrc: item.dataset.itemFullSrc,
            fullSrcWebp: item.dataset.itemFullSrcWebp,
            width: parseInt(item.dataset.itemWidth) || null,
            height: parseInt(item.dataset.itemHeight) || null,
            alt: item.dataset.itemAlt || '',
            title: item.dataset.itemTitle || '',
            caption: item.dataset.itemCaption || '',
            loaded: false,
        }));
    }
    
    // =========================================
    // ✅ CAROUSEL (SPLIDE)
    // =========================================
    
    /**
     * Initialiser le carousel avec Splide
     */
    initCarousel() {
        // Vérifier que Splide est disponible
        if (typeof Splide === 'undefined') {
            console.error('❌ Splide.js not loaded! Falling back to grid layout.');
            this.initLazyLoad();
            this.bindEvents();
            return;
        }
        
        console.log(`🎠 Initializing carousel for gallery: ${this.id}`);
        
        // Transformer la structure HTML en structure Splide
        // this.transformToSplideStructure();
        
        // Configurer les options Splide
        const options = this.buildSplideOptions();
        
        console.log('⚙️ Splide options:', options);
        
        try {
            // Créer l'instance Splide
            this.splideInstance = new Splide(this.element.querySelector('.splide'), options);
            
            // Événements Splide
            this.splideInstance.on('mounted', () => {
                console.log(`✅ Gallery carousel mounted: ${this.id}`);
            });
            
            this.splideInstance.on('move', (newIndex) => {
                this.currentIndex = newIndex;
            });
            
            // Si lightbox activé, ajouter les listeners de clic
            if (this.config.lightbox) {
                this.splideInstance.on('click', (slide) => {
                    const index = slide.index;
                    this.openLightbox(index);
                });
            }
            
            // Monter le carousel
            this.splideInstance.mount();
            
            // Stocker l'instance
            carouselInstances.set(this.id, this.splideInstance);
            
            // Initialiser le lightbox si activé
            if (this.config.lightbox) {
                this.initLightbox();
            }
            
            console.log(`✅ Gallery carousel initialized: ${this.id}`);
            
        } catch (error) {
            console.error('❌ Error initializing gallery carousel:', error);
        }
    }
    
    /**
     * Transformer la structure HTML existante en structure Splide
     */
    transformToSplideStructure() {
        // Récupérer le conteneur grid existant
        const gridContainer = this.element.querySelector('.grid');
        if (!gridContainer) {
            console.error('❌ No grid container found in gallery');
            return;
        }
        
        // Récupérer tous les items
        const items = Array.from(gridContainer.querySelectorAll('.gallery-item'));
        
        if (items.length === 0) {
            console.warn('⚠️ No gallery items found');
            return;
        }
        
        // Calculer le gap en pixels
        const gapValue = gapMap[this.config.gap] || this.config.gap || '1rem';
        
        // Créer la structure Splide
        const splideContainer = document.createElement('div');
        splideContainer.className = 'splide gallery-carousel';
        splideContainer.id = `splide-${this.id}`;
        
        // Track
        const track = document.createElement('div');
        track.className = 'splide__track';
        
        // List
        const list = document.createElement('ul');
        list.className = 'splide__list';
        
        // Transformer chaque item en slide Splide
        items.forEach((item, index) => {
            const slide = document.createElement('li');
            slide.className = 'splide__slide';
            
            // Récupérer les données de l'item
            const img = item.querySelector('img');
            const src = item.dataset.itemSrc || img?.src || '';
            const alt = item.dataset.itemAlt || img?.alt || '';
            const title = item.dataset.itemTitle || '';
            const caption = item.dataset.itemCaption || '';
            
            // Créer le contenu du slide
            slide.innerHTML = `
                <div class="splide__slide__container gallery-slide-content relative overflow-hidden ${this.config.rounded}" 
                     data-item-index="${index}"
                     data-item-id="${item.dataset.itemId || ''}"
                     data-item-src="${src}"
                     data-item-full-src="${item.dataset.itemFullSrc || src}"
                     data-item-alt="${alt}"
                     data-item-title="${title}"
                     data-item-caption="${caption}">
                    <picture class="w-full h-full">
                        <img src="${src}" 
                             alt="${alt}" 
                             ${title ? `title="${title}"` : ''}
                             class="w-full h-full object-cover ${this.config.imageShadow} ${this.config.hoverEffect === 'zoom' ? 'transition-transform duration-500 hover:scale-105' : ''}"
                             loading="lazy"
                             decoding="async"
                             onerror="this.onerror=null;this.src='/images/placeholders/default_fr.svg'">
                    </picture>
                    ${this.config.lightbox ? `
                        <div class="absolute inset-0 bg-black/0 hover:bg-black/20 transition-colors cursor-pointer flex items-center justify-center group">
                            <i class="fas fa-expand text-white text-xl opacity-0 group-hover:opacity-100 transition-opacity"></i>
                        </div>
                    ` : ''}
                </div>
                ${this.config.showCaptions && caption ? `
                    <div class="gallery-slide-caption text-center mt-2 px-2">
                        ${title ? `<h4 class="text-sm font-medium text-gray-900 dark:text-white">${title}</h4>` : ''}
                        <p class="text-xs text-gray-600 dark:text-gray-400">${caption}</p>
                    </div>
                ` : ''}
            `;
            
            list.appendChild(slide);
        });
        
        track.appendChild(list);
        splideContainer.appendChild(track);
        
        // Remplacer le grid par la structure Splide
        gridContainer.replaceWith(splideContainer);
        
        // Mettre à jour les items parsés avec les nouveaux éléments
        this.parseItemsFromSplide();
        
        console.log(`🔄 Transformed ${items.length} items to Splide structure`);
    }
    
    /**
     * Re-parser les items après transformation Splide
     */
    parseItemsFromSplide() {
        const slides = this.element.querySelectorAll('.splide__slide__container');
        
        this.items = Array.from(slides).map((slide, index) => ({
            element: slide,
            index: parseInt(slide.dataset.itemIndex) || index,
            id: slide.dataset.itemId,
            src: slide.dataset.itemSrc,
            fullSrc: slide.dataset.itemFullSrc || slide.dataset.itemSrc,
            alt: slide.dataset.itemAlt || '',
            title: slide.dataset.itemTitle || '',
            caption: slide.dataset.itemCaption || '',
            loaded: true,
        }));
    }
    
    /**
     * Construire les options Splide depuis la config
     */
buildSplideOptions() {
    const { columns, gap, carousel } = this.config;
    const gapValue = gapMap[gap] || gap || '1rem';
    console.log(carousel);
    
    const options = {
        type: carousel.loop ? 'loop' : 'slide',
        perPage: columns,
        perMove: carousel.perMove,
        gap: gapValue,
        autoplay: carousel.autoplay,
        interval: carousel.interval,
        speed: carousel.speed,
        arrows: carousel.arrows,
        pagination: carousel.pagination,
        drag: carousel.drag,
        pauseOnHover: carousel.pauseOnHover,
        lazyLoad: 'nearby',
        preloadPages: 1,
        
        // ✅ NE PAS utiliser heightRatio - laisser le CSS gérer l'aspect ratio
        // ✅ NE PAS utiliser autoHeight car ça cause des problèmes
        // ✅ NE PAS utiliser fixedHeight
        
        classes: {
            arrows: 'splide__arrows gallery-arrows',
            arrow: 'splide__arrow gallery-arrow',
            prev: 'splide__arrow--prev gallery-arrow-prev',
            next: 'splide__arrow--next gallery-arrow-next',
            pagination: 'splide__pagination gallery-pagination',
            page: 'splide__pagination__page gallery-dot',
        },
        
        breakpoints: {
            640: {
                perPage: Math.min(1, columns),
                gap: '0.5rem',
            },
            768: {
                perPage: Math.min(2, columns),
                gap: '0.75rem',
            },
            1024: {
                perPage: Math.min(columns, 3),
                gap: gapValue,
            },
        },
    };
    
    // ✅ NE PAS ajouter heightRatio du tout - le CSS gère l'aspect ratio
    
    return options;
}
    
    /**
     * Attacher les événements
     */
    bindEvents() {
        // Clic sur les items pour ouvrir le lightbox (mode non-carousel)
        if (this.config.lightbox && this.config.layoutType !== 'carousel') {
            this.items.forEach((item, index) => {
                const clickTarget = item.element.querySelector('a[data-lightbox]') || item.element;
                clickTarget.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.openLightbox(index);
                });
                
                // Ajouter cursor pointer si pas de lien
                if (!item.element.querySelector('a[data-lightbox]')) {
                    item.element.style.cursor = 'pointer';
                }
            });
        }
        
        // Pour le carousel, les événements de clic sont gérés via Splide
        if (this.config.layoutType === 'carousel' && this.config.lightbox) {
            const slides = this.element.querySelectorAll('.splide__slide__container');
            slides.forEach((slide, index) => {
                slide.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.openLightbox(index);
                });
            });
        }
    }
    
    // =========================================
    // LAZY LOADING
    // =========================================
    
    /**
     * Initialiser le lazy loading avec IntersectionObserver
     */
    initLazyLoad() {
        if (!('IntersectionObserver' in window)) {
            this.loadAllImages();
            return;
        }
        
        const options = {
            root: null,
            rootMargin: this.config.lazyLoadRootMargin,
            threshold: this.config.lazyLoadThreshold,
        };
        
        this.lazyObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    this.loadImage(entry.target);
                    this.lazyObserver.unobserve(entry.target);
                }
            });
        }, options);
        
        this.items.forEach(item => {
            const img = item.element.querySelector('img');
            if (img && !img.complete) {
                this.lazyObserver.observe(img);
            }
        });
    }
    
    /**
     * Générer un placeholder SVG
     */
    generatePlaceholder(width, height) {
        const w = width || 400;
        const h = height || 300;
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">
            <rect fill="#f3f4f6" width="100%" height="100%"/>
        </svg>`;
        return `data:image/svg+xml;base64,${btoa(svg)}`;
    }
    
    /**
     * Charger une image
     */
    loadImage(img) {
        const originalSrc = img.dataset.src;
        const originalSrcset = img.dataset.srcset;
        
        if (!originalSrc) return;
        
        const preloader = new Image();
        
        preloader.onload = () => {
            img.src = originalSrc;
            if (originalSrcset) {
                img.srcset = originalSrcset;
            }
            
            requestAnimationFrame(() => {
                img.classList.remove('opacity-0');
                img.classList.add('opacity-100');
            });
            
            delete img.dataset.src;
            delete img.dataset.srcset;
            
            const itemIndex = this.items.findIndex(item => 
                item.element.contains(img)
            );
            if (itemIndex !== -1) {
                this.items[itemIndex].loaded = true;
            }
            
            img.dispatchEvent(new CustomEvent('gallery:image-loaded', {
                bubbles: true,
                detail: { src: originalSrc }
            }));
        };
        
        preloader.onerror = () => {
            img.classList.add('gallery-error');
            console.warn(`Failed to load image: ${originalSrc}`);
        };
        
        preloader.src = originalSrc;
    }
    
    /**
     * Charger toutes les images (fallback)
     */
    loadAllImages() {
        this.items.forEach(item => {
            const img = item.element.querySelector('img');
            if (img && img.dataset.src) {
                this.loadImage(img);
            }
        });
    }
    
    // =========================================
    // LIGHTBOX
    // =========================================
    
    /**
     * Initialiser le lightbox
     */
    initLightbox() {
        if (!document.getElementById('gallery-lightbox')) {
            this.createLightboxElement();
        }
        
        this.lightboxElement = document.getElementById('gallery-lightbox');
    }
    
    /**
     * Créer l'élément DOM du lightbox
     */
    createLightboxElement() {
        const lightbox = document.createElement('div');
        lightbox.id = 'gallery-lightbox';
        lightbox.className = 'fixed inset-0 z-[9999] hidden';
        lightbox.setAttribute('role', 'dialog');
        lightbox.setAttribute('aria-modal', 'true');
        lightbox.setAttribute('aria-label', 'Galerie d\'images');
        
        lightbox.innerHTML = `
            <!-- Backdrop -->
            <div class="lightbox-backdrop absolute inset-0 bg-black/95 backdrop-blur-sm" data-lightbox-close></div>
            
            <!-- Container -->
            <div class="lightbox-container relative w-full h-full flex flex-col">
                
                <!-- Header -->
                <div class="lightbox-header flex items-center justify-between p-4 text-white">
                    <div class="lightbox-counter text-sm font-medium">
                        <span class="lightbox-current">1</span> / <span class="lightbox-total">1</span>
                    </div>
                    <div class="lightbox-actions flex items-center gap-2">
                        <button type="button" class="lightbox-zoom p-2 hover:bg-white/10 rounded-lg transition-colors" title="Zoom">
                            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"/>
                            </svg>
                        </button>
                        <button type="button" class="lightbox-close p-2 hover:bg-white/10 rounded-lg transition-colors" data-lightbox-close title="Fermer (Échap)">
                            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                            </svg>
                        </button>
                    </div>
                </div>
                
                <!-- Main content -->
                <div class="lightbox-main flex-1 flex items-center justify-center relative px-4 overflow-hidden">
                    
                    <!-- Navigation prev -->
                    <button type="button" class="lightbox-prev absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-all z-10" title="Précédent">
                        <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
                        </svg>
                    </button>
                    
                    <!-- Image container -->
                    <div class="lightbox-image-wrapper max-w-full max-h-full flex items-center justify-center">
                        <picture class="lightbox-picture">
                            <source class="lightbox-source-webp" type="image/webp" srcset="">
                            <img class="lightbox-image max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl transition-transform duration-300" 
                                 src="" alt="" loading="eager">
                        </picture>
                        
                        <!-- Loading spinner -->
                        <div class="lightbox-loading absolute inset-0 flex items-center justify-center hidden">
                            <div class="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
                        </div>
                    </div>
                    
                    <!-- Navigation next -->
                    <button type="button" class="lightbox-next absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-all z-10" title="Suivant">
                        <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                        </svg>
                    </button>
                </div>
                
                <!-- Footer with caption -->
                <div class="lightbox-footer p-4 text-center text-white">
                    <h3 class="lightbox-title text-lg font-semibold"></h3>
                    <p class="lightbox-caption text-sm text-white/70 mt-1"></p>
                </div>
                
            </div>
            
            <!-- Thumbnails (optional) -->
            <div class="lightbox-thumbnails hidden absolute bottom-20 left-0 right-0 px-4">
                <div class="lightbox-thumbs-container flex gap-2 justify-center overflow-x-auto py-2"></div>
            </div>
        `;
        
        document.body.appendChild(lightbox);
        this.bindLightboxEvents(lightbox);
    }
    
    /**
     * Attacher les événements du lightbox
     */
    bindLightboxEvents(lightbox) {
        lightbox.querySelectorAll('[data-lightbox-close]').forEach(el => {
            el.addEventListener('click', () => this.closeLightbox());
        });
        
        lightbox.querySelector('.lightbox-prev').addEventListener('click', () => this.prevImage());
        lightbox.querySelector('.lightbox-next').addEventListener('click', () => this.nextImage());
        
        document.addEventListener('keydown', (e) => this.handleKeyboard(e));
        
        const main = lightbox.querySelector('.lightbox-main');
        main.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: true });
        main.addEventListener('touchend', (e) => this.handleTouchEnd(e), { passive: true });
        
        lightbox.querySelector('.lightbox-zoom').addEventListener('click', () => this.toggleZoom());
    }
    
    /**
     * Ouvrir le lightbox
     */
    openLightbox(index = 0) {
        if (!this.lightboxElement || !this.items.length) return;
        
        this.currentIndex = index;
        this.isLightboxOpen = true;
        
        this.lightboxElement.classList.remove('hidden');
        this.lightboxElement.classList.add('flex');
        
        document.body.style.overflow = 'hidden';
        
        this.showImage(index);
        
        this.lightboxElement.querySelector('.lightbox-total').textContent = this.items.length;
        
        this.lightboxElement.focus();
        
        if (this.config.preloadAdjacent) {
            this.preloadAdjacentImages(index);
        }
        
        this.element.dispatchEvent(new CustomEvent('gallery:lightbox-open', {
            bubbles: true,
            detail: { index, item: this.items[index] }
        }));
    }
    
    /**
     * Fermer le lightbox
     */
    closeLightbox() {
        if (!this.lightboxElement) return;
        
        this.isLightboxOpen = false;
        
        this.lightboxElement.classList.add('hidden');
        this.lightboxElement.classList.remove('flex');
        
        document.body.style.overflow = '';
        
        const img = this.lightboxElement.querySelector('.lightbox-image');
        img.style.transform = '';
        
        this.element.dispatchEvent(new CustomEvent('gallery:lightbox-close', {
            bubbles: true,
            detail: { index: this.currentIndex }
        }));
    }
    
    /**
     * Afficher une image dans le lightbox
     */
    showImage(index) {
        const item = this.items[index];
        if (!item) return;
        
        const img = this.lightboxElement.querySelector('.lightbox-image');
        const sourceWebp = this.lightboxElement.querySelector('.lightbox-source-webp');
        const loading = this.lightboxElement.querySelector('.lightbox-loading');
        const title = this.lightboxElement.querySelector('.lightbox-title');
        const caption = this.lightboxElement.querySelector('.lightbox-caption');
        const counter = this.lightboxElement.querySelector('.lightbox-current');
        
        loading.classList.remove('hidden');
        img.classList.add('opacity-0');
        
        const fullSrc = item.fullSrc || item.src;
        const fullSrcWebp = item.fullSrcWebp || item.srcWebp;
        
        if (fullSrcWebp) {
            sourceWebp.srcset = fullSrcWebp;
        } else {
            sourceWebp.srcset = '';
        }
        
        const preloader = new Image();
        preloader.onload = () => {
            img.src = fullSrc;
            img.alt = item.alt;
            
            loading.classList.add('hidden');
            img.classList.remove('opacity-0');
        };
        preloader.onerror = () => {
            loading.classList.add('hidden');
            console.warn(`Failed to load lightbox image: ${fullSrc}`);
        };
        preloader.src = fullSrc;
        
        title.textContent = item.title || '';
        caption.textContent = item.caption || '';
        counter.textContent = index + 1;
        
        const footer = this.lightboxElement.querySelector('.lightbox-footer');
        if (this.config.showCaptions && (item.title || item.caption)) {
            footer.classList.remove('hidden');
        } else {
            footer.classList.add('hidden');
        }
        
        this.updateNavigation();
    }
    
    /**
     * Mettre à jour la visibilité des boutons de navigation
     */
    updateNavigation() {
        const prevBtn = this.lightboxElement.querySelector('.lightbox-prev');
        const nextBtn = this.lightboxElement.querySelector('.lightbox-next');
        
        prevBtn.style.opacity = this.currentIndex === 0 ? '0.3' : '1';
        nextBtn.style.opacity = this.currentIndex === this.items.length - 1 ? '0.3' : '1';
    }
    
    /**
     * Image précédente
     */
    prevImage() {
        if (this.currentIndex > 0) {
            this.currentIndex--;
            this.showImage(this.currentIndex);
            this.preloadAdjacentImages(this.currentIndex);
        }
    }
    
    /**
     * Image suivante
     */
    nextImage() {
        if (this.currentIndex < this.items.length - 1) {
            this.currentIndex++;
            this.showImage(this.currentIndex);
            this.preloadAdjacentImages(this.currentIndex);
        }
    }
    
    /**
     * Précharger les images adjacentes
     */
    preloadAdjacentImages(index) {
        const indices = [index - 1, index + 1].filter(i => i >= 0 && i < this.items.length);
        
        indices.forEach(i => {
            const item = this.items[i];
            const src = item.fullSrc || item.src;
            if (src) {
                const preloader = new Image();
                preloader.src = src;
            }
        });
    }
    
    /**
     * Gérer les événements clavier
     */
    handleKeyboard(e) {
        if (!this.isLightboxOpen) return;
        if (!this.config.keyboardNavigation && e.key !== 'Escape') return;
        
        switch (e.key) {
            case 'Escape':
                this.closeLightbox();
                break;
            case 'ArrowLeft':
                e.preventDefault();
                this.prevImage();
                break;
            case 'ArrowRight':
                e.preventDefault();
                this.nextImage();
                break;
            case 'Home':
                e.preventDefault();
                this.currentIndex = 0;
                this.showImage(0);
                break;
            case 'End':
                e.preventDefault();
                this.currentIndex = this.items.length - 1;
                this.showImage(this.currentIndex);
                break;
        }
    }
    
    /**
     * Gérer le début du touch
     */
    handleTouchStart(e) {
        this.touchStartX = e.changedTouches[0].screenX;
    }
    
    /**
     * Gérer la fin du touch (swipe)
     */
    handleTouchEnd(e) {
        this.touchEndX = e.changedTouches[0].screenX;
        const diff = this.touchStartX - this.touchEndX;
        const threshold = 50;
        
        if (Math.abs(diff) > threshold) {
            if (diff > 0) {
                this.nextImage();
            } else {
                this.prevImage();
            }
        }
    }
    
    /**
     * Toggle zoom sur l'image
     */
    toggleZoom() {
        const img = this.lightboxElement.querySelector('.lightbox-image');
        const isZoomed = img.style.transform === 'scale(2)';
        
        img.style.transform = isZoomed ? '' : 'scale(2)';
        img.style.cursor = isZoomed ? '' : 'zoom-out';
    }
    
    // =========================================
    // API PUBLIQUE
    // =========================================
    
    /**
     * Rafraîchir la galerie (après ajout/suppression d'images)
     */
    refresh() {
        // Pour le carousel, refresh Splide
        if (this.config.layoutType === 'carousel' && this.splideInstance) {
            this.splideInstance.refresh();
            this.parseItemsFromSplide();
            return;
        }
        
        this.parseItems();
        
        if (this.config.lazyLoad && this.lazyObserver) {
            this.items.forEach(item => {
                const img = item.element.querySelector('img');
                if (img && !img.complete && img.dataset.src) {
                    this.lazyObserver.observe(img);
                }
            });
        }
    }
    
    /**
     * Aller à une image/slide spécifique
     */
    goTo(index) {
        if (index >= 0 && index < this.items.length) {
            // Pour le carousel, utiliser Splide.go()
            if (this.config.layoutType === 'carousel' && this.splideInstance) {
                this.splideInstance.go(index);
            }
            
            if (this.isLightboxOpen) {
                this.currentIndex = index;
                this.showImage(index);
            } else if (this.config.lightbox) {
                this.openLightbox(index);
            }
        }
    }
    
    /**
     * Détruire la galerie
     */
    destroy() {
        // Détruire Splide si c'est un carousel
        if (this.splideInstance) {
            this.splideInstance.destroy();
            carouselInstances.delete(this.id);
        }
        
        if (this.lazyObserver) {
            this.lazyObserver.disconnect();
        }
        
        this.element.removeAttribute('data-gallery-initialized');
        galleries.delete(this.id);
    }
}

/**
 * Obtenir une instance de galerie par ID
 * @param {string} id - ID de la galerie
 * @returns {Gallery|undefined}
 */
export function getGallery(id) {
    return galleries.get(id);
}

/**
 * Obtenir toutes les instances de galeries
 * @returns {Map}
 */
export function getAllGalleries() {
    return galleries;
}

/**
 * Obtenir une instance Splide de carousel par ID
 * @param {string} id - ID de la galerie
 * @returns {Splide|undefined}
 */
export function getCarouselInstance(id) {
    return carouselInstances.get(id);
}

/**
 * Rafraîchir toutes les galeries
 */
export function refreshAllGalleries() {
    galleries.forEach(gallery => gallery.refresh());
}

// Export de la classe pour utilisation avancée
export { Gallery };

// Auto-init si pas dans un module
if (typeof window !== 'undefined') {
    window.GalleryComponent = {
        init: initGallery,
        get: getGallery,
        getAll: getAllGalleries,
        getCarousel: getCarouselInstance,
        refresh: refreshAllGalleries,
        Gallery,
    };
}
